import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiRequest, exportOrders } from '../../utils/apiHelper';
import { formatPrice } from '../../utils/formatHelper';

import { triggerNotificationRefresh } from '../../utils/notificationsApi';
import { useDataTable } from '../hooks/useDataTable';
import { useModal } from '../hooks/useModal';
import DataTable from '../components/shared/DataTable';
import PageHeader from '../components/shared/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import FilterBar from '../components/shared/FilterBar';
import ConfirmModal from '../components/shared/ConfirmModal';
import PromptModal from '../components/shared/PromptModal';
import ActionDrawer from '../components/shared/ActionDrawer';
import OrderDetails from './OrderDetails';
import ShipmentModal from './ShipmentModal';
import CreateOrderModal from './CreateOrderModal';
import { ORDER_STATUS, ORDER_STATUS_TEXT } from '../../constants/orderConstants';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { showSuccess, showError } from '../../utils/toastHelper';

function OrderManagement() {
  const { user } = useAuth();
  // İptal / iade / toplu sipariş tetikleme yalnızca yöneticilere açıktır;
  // backend de aynı kuralı uyguluyor — UI tutarlılığı için burada gizliyoruz.
  const isManager = ['SUPER_ADMIN', 'COMPANY_ADMIN'].includes(user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const handledQueryRef = useRef({ orderId: null, status: null });
  const isFirstMount = useRef(true);

  // URL parametrelerini mount anında oku (useDataTable'dan ÖNCE)
  const initialParams = useRef(new URLSearchParams(location.search || '')).current;
  const initialSubscriptionId = initialParams.get('subscriptionId') || '';

  const {
    isOpen: isShipmentOpen,
    open: openShipment,
    close: closeShipment
  } = useModal();
  const {
    isOpen: isCreateOrderOpen,
    open: openCreateOrder,
    close: closeCreateOrder
  } = useModal();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [showRefundPrompt, setShowRefundPrompt] = useState(false);
  const [showCreateOrdersConfirm, setShowCreateOrdersConfirm] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [orderToRefund, setOrderToRefund] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: orders,
    loading,
    pagination,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleFilterChange,
    filters,
    refresh
  } = useDataTable('/orders', {
    initialFilters: {
      status: 'all',
      startDate: '',
      endDate: '',
      subscriptionId: initialSubscriptionId
    }
  });

  const {
    isOpen: isDetailsOpen,
    open: openDetails,
    close: closeDetails
  } = useModal();

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    openDetails();
  };

  // Sync URL query params → filters & open order details
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const status = params.get('status');
    const orderId = params.get('orderId');
    const subscriptionId = params.get('subscriptionId') || '';

    // Status filter from query
    if (status && handledQueryRef.current.status !== status && filters?.status !== status) {
      handledQueryRef.current.status = status;
      handleFilterChange('status', status);
    }

    // subscriptionId: İlk mount'ta initialFilters zaten hallediyor, skip et.
    // Sonraki navigasyonlarda (farklı abonelik seçildiğinde) güncelle.
    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else if (subscriptionId !== (filters.subscriptionId || '')) {
      handleFilterChange('subscriptionId', subscriptionId);
    }

    // Open order details from query
    if (!orderId || handledQueryRef.current.orderId === orderId) return;
    handledQueryRef.current.orderId = orderId;

    (async () => {
      try {
        const res = await apiRequest(`/orders/${encodeURIComponent(orderId)}`);
        if (!res?.success || !res?.order) {
          throw new Error(res?.message || 'Sipariş bulunamadı');
        }
        handleViewDetails(res.order);
      } catch (err) {
        console.error(err);
        showError(err?.message || 'Sipariş detayları açılamadı');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleViewShipment = (order) => {
    setSelectedOrder(order);
    openShipment();
  };

  const handleShipmentSuccess = (shipment, shouldRefresh = false) => {
    if (shouldRefresh) {
      refresh();
    }
    closeShipment();
  };

  const [cancelReason, setCancelReason] = useState('');
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);

  // Actions
  const handleOrderCancel = (orderId) => {
    setOrderToCancel(orderId);
    setShowCancelPrompt(true);
  };

  const handleCancelPromptConfirm = (reason) => {
    if (!reason) return;
    setCancelReason(reason);
    setShowCancelPrompt(false);
    setShowCancelConfirm(true);
  };

  const confirmCancel = async () => {
    if (!orderToCancel || !cancelReason) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`/orders/${orderToCancel}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({ cancelReason })
      });

      if (response.success) {
        showSuccess('Sipariş başarıyla iptal edildi');
        setShowCancelConfirm(false);
        setOrderToCancel(null);
        setCancelReason('');
        refresh();
      } else {
        showError(response.message || 'Hata oluştu');
      }
    } catch (error) {
      console.error(error);
      showError('Sipariş iptal edilirken bir hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOrderRefund = (orderId) => {
    setOrderToRefund(orderId);
    setShowRefundPrompt(true);
  };

  const [refundReason, setRefundReason] = useState('');

  const handleRefundPromptConfirm = (reason) => {
    if (!reason) return;
    setRefundReason(reason);
    setShowRefundPrompt(false);
    setShowRefundConfirm(true);
  };

  const confirmRefund = async () => {
    if (!orderToRefund || !refundReason) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`/orders/${orderToRefund}/refund`, {
        method: 'POST',
        body: JSON.stringify({ refundReason })
      });

      if (response.success) {
        showSuccess(response.message || 'Sipariş başarıyla iade edildi');
        setShowRefundConfirm(false);
        setOrderToRefund(null);
        setRefundReason('');
        refresh();
      } else {
        showError(response.message || 'Hata oluştu');
      }
    } catch (error) {
      console.error(error);
      showError(error.data?.message || error.message || 'Sipariş iade edilirken bir hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateAllTracking = async () => {
    try {
      const response = await apiRequest('/shipping/tracking-update-all', {
        method: 'POST'
      });

      if (response.success) {
        showSuccess('Kargo takip güncelleme işlemi başlatıldı');
        refresh();
      } else {
        showError(response.message || 'Kargo takip güncellenemedi');
      }
    } catch (error) {
      console.error(error);
      showError('Kargo takip güncellenemedi');
    }
  };

  const handleCreateSubscriptionOrders = () => {
    setShowCreateOrdersConfirm(true);
  };

  const confirmCreateSubscriptionOrders = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest('/orders/create-subscription-orders', {
        method: 'POST'
      });

      if (response.success) {
        showSuccess('Abonelik siparişleri oluşturma başladı');
        setShowCreateOrdersConfirm(false);
        refresh();
        // Trigger notification refresh after a short delay (to allow backend job to complete)
        setTimeout(() => triggerNotificationRefresh(), 3000);
      } else {
        showError(response.message || 'Hata oluştu');
      }
    } catch (error) {
      console.error(error);
      showError('Hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearFilters = () => {
    handleFilterChange('status', 'all');
    handleFilterChange('startDate', '');
    handleFilterChange('endDate', '');
    handleFilterChange('subscriptionId', '');
    handleSearch('');
    // URL'deki subscriptionId parametresini temizle
    handledQueryRef.current.subscriptionId = null;
    navigate('/admin/orders', { replace: true });
  };

  const handleExportOrders = async () => {
    try {
      await exportOrders(filters);
      showSuccess('Siparişler başarıyla indirildi');
    } catch (err) {
      showError(err.message || 'Dışa aktarma sırasında hata oluştu');
    }
  };

  // Columns Configuration
  const columns = [
    {
      header: 'Sipariş No',
      accessor: 'orderNumber',
      render: (row) => (
        <div>
          <span className="fw-bold text-primary">#{row.orderNumber}</span>
          <div className="small text-muted">{row.products?.length} ürün</div>
        </div>
      )
    },
    {
      header: 'Müşteri',
      accessor: 'user',
      render: (row) => (
        <div className="d-flex align-items-center">
          <div className="avatar-circle bg-light me-2 text-dark d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%' }}>
            <span className="fw-medium small">{row.user?.fullName?.charAt(0) || <i className="fas fa-user small"></i>}</span>
          </div>
          <div>
            <div className="fw-bold text-dark small">{row.user?.fullName}</div>
            <div className="text-muted small">{row.user?.email}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Tutar',
      accessor: 'totalAmount',
      render: (row) => <span className="fw-medium text-dark">{formatPrice(row.totalAmount)}</span>
    },
    {
      header: 'Durum',
      accessor: 'status',
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Dönem',
      accessor: 'paymentPeriod',
      render: (row) => {
        if (!row.subscriptionId) return <span className="text-muted small">—</span>;

        const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

        const period = row.paymentDetails?.paymentPeriod;
        let monthName, year, half = '';

        if (period) {
          const parts = period.split('-');
          year = parts[0];
          monthName = months[parseInt(parts[1], 10) - 1] || parts[1];
          // Biweekly: "2026-04-W1" → "(1. Yarı)", "2026-04-W2" → "(2. Yarı)"
          if (parts[2]) {
            const weekNum = parts[2].replace('W', '');
            half = ` (${weekNum}. Yarı)`;
          }
        } else {
          const date = new Date(row.createdAt);
          monthName = months[date.getMonth()];
          year = date.getFullYear();
        }

        return (
          <span className="badge bg-light text-dark border small">
            <i className="fas fa-calendar-alt me-1 text-muted"></i>
            {monthName} {year}{half}
          </span>
        );
      }
    },
    {
      header: 'Tarih',
      accessor: 'createdAt',
      render: (row) => <span className="text-muted small">{new Date(row.createdAt).toLocaleDateString('tr-TR')}</span>
    },
    {
      header: 'İşlemler',
      accessor: 'actions',
      render: (row) => (
        <div className="dropdown" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-light text-muted" data-bs-toggle="dropdown">
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
            <li>
              <button className="dropdown-item" onClick={() => handleViewDetails(row)}>
                <i className="fas fa-eye fa-fw me-2"></i> Detaylar
              </button>
            </li>
            {['PENDING', 'PROCESSING', 'SHIPPED'].includes(row.status) && (
              <li>
                <button className="dropdown-item" onClick={() => handleViewShipment(row)}>
                  <i className="fas fa-truck fa-fw me-2"></i> Kargo İşlemleri
                </button>
              </li>
            )}
            {isManager && (
              <>
                <li><hr className="dropdown-divider" /></li>
                {['PENDING', 'PROCESSING'].includes(row.status) && (
                  <li>
                    <button className="dropdown-item text-danger" onClick={() => handleOrderCancel(row.id)}>
                      <i className="fas fa-times fa-fw me-2"></i> İptal Et
                    </button>
                  </li>
                )}
                <li>
                  <button className="dropdown-item" onClick={() => handleOrderRefund(row.id)}>
                    <i className="fas fa-undo fa-fw me-2"></i> İade Et
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      )
    }
  ];

  // Show full page loader on initial load
  if (loading && (!orders || orders.length === 0)) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Siparişler yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="Sipariş Yönetimi"
        description="Tüm siparişleri buradan yönetebilirsiniz."
        breadcrumbs={[{ label: 'Admin' }, { label: 'Siparişler' }]}
        actions={
          <div className="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
            <button className="btn btn-white border" onClick={handleExportOrders}>
              <i className="fas fa-file-csv me-2"></i> Dışa Aktar
            </button>
            <button className="btn btn-white border" onClick={handleUpdateAllTracking}>
              <i className="fas fa-sync-alt me-2"></i> Takip Güncelle
            </button>
            {isManager && (
              <button className="btn btn-white border" onClick={handleCreateSubscriptionOrders}>
                <i className="fas fa-calendar-plus me-2"></i> Bekleyen Siparişleri Oluştur
              </button>
            )}
            <button className="btn btn-primary" onClick={openCreateOrder}>
              <i className="fas fa-plus me-2"></i> Manuel Sipariş
            </button>
          </div>
        }
      />

      {/* Subscription filter banner */}
      {filters.subscriptionId && (
        <div className="alert alert-info d-flex align-items-center justify-content-between py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
          <div>
            <i className="fas fa-filter me-2"></i>
            <strong>Abonelik #{filters.subscriptionId}</strong> için siparişler gösteriliyor
          </div>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={handleClearFilters}
          >
            <i className="fas fa-times me-1"></i> Tüm Siparişleri Göster
          </button>
        </div>
      )}

      {/* Filters */}
      <FilterBar
        filters={[
          {
            type: 'select',
            key: 'status',
            label: 'Durum',
            defaultValue: 'all',
            options: [
              { value: 'all', label: 'Tüm Durumlar' },
              ...Object.keys(ORDER_STATUS).map(key => ({
                value: key,
                label: ORDER_STATUS_TEXT[key]
              }))
            ]
          },
          {
            type: 'date',
            key: 'startDate',
            label: 'Başlangıç Tarihi',
            placeholder: 'Başlangıç Tarihi'
          },
          {
            type: 'date',
            key: 'endDate',
            label: 'Bitiş Tarihi',
            placeholder: 'Bitiş Tarihi',
            min: filters.startDate
          }
        ]}
        activeFilters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchPlaceholder="Sipariş, Müşteri Ara..."
        showSearch={true}
        showClearButton={true}
      />

      <DataTable
        tableId="orders"
        columns={columns}
        data={orders}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onRowClick={(row) => handleViewDetails(row)}
        emptyState={{
          title: 'Sipariş Bulunamadı',
          message: 'Gösterilecek sipariş bulunmuyor.',
          icon: 'shopping-cart'
        }}
      />
      {/* Detail Drawer */}
      <ActionDrawer
        isOpen={isDetailsOpen}
        onClose={closeDetails}
        title={
          <div className="d-flex align-items-center">
            <span className="fw-bold me-3 text-dark fs-5">#{selectedOrder?.orderNumber}</span>
            {selectedOrder && <StatusBadge status={selectedOrder.status} />}
          </div>
        }
        width="700px"
      >
        <OrderDetails
          order={selectedOrder}
          onAssignCarrier={() => {
            closeDetails();
            handleViewShipment(selectedOrder);
          }}
        />
      </ActionDrawer>

      <ShipmentModal
        isOpen={isShipmentOpen}
        order={selectedOrder}
        onClose={closeShipment}
        onSuccess={(updatedOrder) => {
          refresh();
          closeShipment();
        }}
      />

      <CreateOrderModal
        isOpen={isCreateOrderOpen}
        onClose={closeCreateOrder}
        onSuccess={() => {
          refresh();
          closeCreateOrder();
        }}
      />

      {/* Confirmation Modals */}
      <PromptModal
        show={showCancelPrompt}
        onClose={() => {
          setShowCancelPrompt(false);
          setOrderToCancel(null);
        }}
        onConfirm={handleCancelPromptConfirm}
        title="Sipariş İptali"
        message="Lütfen iptal nedenini giriniz:"
        placeholder="İptal nedeni..."
        confirmText="Devam Et"
        cancelText="İptal"
        variant="warning"
        type="textarea"
        required={true}
      />

      <ConfirmModal
        show={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setOrderToCancel(null);
          setCancelReason('');
        }}
        onConfirm={confirmCancel}
        title="Siparişi İptal Et"
        message="Bu siparişi iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="İptal Et"
        cancelText="Vazgeç"
        variant="danger"
        loading={actionLoading}
      />

      <PromptModal
        show={showRefundPrompt}
        onClose={() => {
          setShowRefundPrompt(false);
          setOrderToRefund(null);
        }}
        onConfirm={handleRefundPromptConfirm}
        title="Sipariş İadesi"
        message="Lütfen iade nedenini giriniz:"
        placeholder="İade nedeni..."
        confirmText="Devam Et"
        cancelText="İptal"
        variant="warning"
        type="textarea"
        required={true}
      />

      <ConfirmModal
        show={showRefundConfirm}
        onClose={() => {
          setShowRefundConfirm(false);
          setOrderToRefund(null);
          setRefundReason('');
        }}
        onConfirm={confirmRefund}
        title="Siparişi İade Et"
        message="Bu siparişi iade etmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="İade Et"
        cancelText="Vazgeç"
        variant="warning"
        loading={actionLoading}
      />

      <ConfirmModal
        show={showCreateOrdersConfirm}
        onClose={() => setShowCreateOrdersConfirm(false)}
        onConfirm={confirmCreateSubscriptionOrders}
        title="Eksik Siparişleri Oluştur"
        message="Ödemesi alınmış ancak siparişi oluşturulmamış abonelikler için siparişleri şimdi oluşturmak istiyor musunuz? Bu işlem normalde otomatik olarak çalışır (ücretli: 30dk, ücretsiz: günlük 09:00)."
        confirmText="Oluştur"
        cancelText="İptal"
        variant="primary"
        loading={actionLoading}
      />
    </div>
  );
}

export default OrderManagement; 